import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Toast from './Toast';
import RinuxEligibility from './RinuxEligibility';
import './Eligibility.css';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { statesList } from '../../data';
import axiosInstance from '../../helpers/axios';

const TENURE_OPTIONS = [3, 6, 9, 12, 18];

const FACILITY_TYPE = {
  1: 'Subvention',
  2: 'Standard Interest',
  3: 'Hybrid',
  4: 'Business to Business',
  5: 'Discounted Interest (No GST)',
};

const SEARCH_ROLES = ['admin', 'cra', 'crm', 'crsa'];

const RINUX_INSTITUTE_ID = '1b6a250b-4a55-49d5-9cfd-eeedad264a8e';

const tfSx = {
  width: 350,
  mb: 2,
  '& label.Mui-focused': { color: '#000' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#707C8B' },
    '&:hover fieldset': { borderColor: '#707C8B' },
    '&.Mui-focused fieldset': { borderColor: '#707C8B' },
  },
};

const tfLabelSx = {
  bgcolor: '#fff8f4',
  fontFamily: 'Outfit',
  fontWeight: 'bold',
  borderRadius: '5px',
  fontSize: '14px',
  lineHeight: '20.25px',
  padding: '2px 8px',
  color: '#000',
};

const tfInputProps = { style: { fontFamily: 'Outfit' } };

// Rinux institute logins get their own screen (RinuxEligibility.js); everyone else gets the default one below
export default function Eligibility() {
  const decode = useCurrentUser();
  return decode?.instituteId === RINUX_INSTITUTE_ID ? <RinuxEligibility /> : <DefaultEligibility />;
}

function DefaultEligibility() {
  const decode = useCurrentUser();
  const role = decode?.role || '';
  const instituteName = decode?.instituteName || '';
  const instituteId = decode?.instituteId || '';

  const [form, setForm] = useState({
    mobile: '', name: '', institute: '', course: '', fees: '', tenure: '', currentState: '', pan: '', dob: '', email: '', studentName: '',
  });
  const [isStudentBorrower, setIsStudentBorrower] = useState(false);
  const [error, setError] = useState('');

  // Institute search
  const [instQuery, setInstQuery] = useState('');
  const [instResults, setInstResults] = useState([]);
  const [instOpen, setInstOpen] = useState(false);
  const [selectedInstId, setSelectedInstId] = useState(null);

  // Course search
  const [courseQuery, setCourseQuery] = useState('');
  const [courseResults, setCourseResults] = useState([]);
  const [courseOpen, setCourseOpen] = useState(false);

  // Products / EMI
  const [tenureProducts, setTenureProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [emiMap, setEmiMap] = useState({});

  // Step navigation: 'form' | 'choose' | 'otp'
  const [step, setStep] = useState('form');

  // OTP step
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [consentChecked, setConsentChecked] = useState(false);
  const otpRefs = useRef([]);

  const [submitting, setSubmitting] = useState(false);
  const [createdApp, setCreatedApp] = useState({ userId: null, applicationId: null });

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState('error');

  const instDebounce = useRef(null);
  const courseDebounce = useRef(null);
  const instDropRef = useRef(null);
  const courseDropRef = useRef(null);

  const handleOtpDigitChange = (index, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const updated = [...otpDigits];
    updated[index] = digit;
    setOtpDigits(updated);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otpDigits[index]) {
        const updated = [...otpDigits];
        updated[index] = '';
        setOtpDigits(updated);
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const updated = Array.from({ length: 6 }, (_, i) => pasted[i] || '');
    setOtpDigits(updated);
    const focusIndex = Math.min(pasted.length, 5);
    otpRefs.current[focusIndex]?.focus();
  };

  const showInstSearch = SEARCH_ROLES.includes(role);

  // ── Effects ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!showInstSearch && instituteName)
      setForm(p => ({ ...p, institute: instituteName }));
  }, [showInstSearch, instituteName]);

  useEffect(() => {
    if (showInstSearch || !instituteId) return;
    setSelectedInstId(instituteId);
    const base = process.env.REACT_APP_BASE_URL;
    fetch(`${base}/products/instituteId/active?instituteId=${instituteId}`, {})
      .then(r => r.json())
      .then(json => {
        const products = json.data || [];
        const sorted = [...products].sort((a, b) => a.tenure - b.tenure);
        setTenureProducts(sorted);
      })
      .catch(() => showToast('Failed to load products. Please refresh.'));
  }, [instituteId, showInstSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (instDropRef.current && !instDropRef.current.contains(e.target)) setInstOpen(false);
      if (courseDropRef.current && !courseDropRef.current.contains(e.target)) setCourseOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!form.tenure || !form.fees || tenureProducts.length === 0) return;
    const cards = tenureProducts.filter(p => p.tenure === Number(form.tenure));
    if (cards.length === 0) return;
    setEmiMap({});
    const base = process.env.REACT_APP_BASE_URL;
    const id = setTimeout(async () => {
      const entries = await Promise.all(
        cards.map(async p => {
          try {
            const res = await fetch(
              `${base}/admin/application/emi-for-product?productId=${p.productId}&loanTenure=${form.tenure}&loanAmount=${form.fees}`,
              {}
            );
            const json = await res.json();
            return [p.productId, json.data];
          } catch {
            return [p.productId, null];
          }
        })
      );
      setEmiMap(Object.fromEntries(entries));
    }, 400);
    return () => clearTimeout(id);
  }, [form.tenure, form.fees, tenureProducts]);

  // ── Helpers ────────────────────────────────────────────────────────

  const showToast = (msg, type = 'error') => {
    setToastMsg(msg);
    setToastType(type);
    setToastVisible(true);
  };

  // ── Institute search ───────────────────────────────────────────────

  const handleInstSearch = (query) => {
    setInstQuery(query);
    setForm(p => ({ ...p, institute: '', course: '', tenure: '' }));
    setSelectedInstId(null);
    setCourseQuery('');
    setCourseResults([]);
    setTenureProducts([]);
    if (instDebounce.current) clearTimeout(instDebounce.current);
    if (!query) { setInstResults([]); setInstOpen(false); return; }
    instDebounce.current = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL;
        const res = await fetch(`${base}/institute/info/name?name=${encodeURIComponent(query)}`, {});
        const json = await res.json();
        const results = json.data || [];
        setInstResults(results);
        setInstOpen(results.length > 0);
      } catch {
        showToast('Failed to search institutes. Please try again.');
      }
    }, 300);
  };

  const selectInst = async (opt) => {
    setInstQuery(opt.brandName);
    setForm(p => ({ ...p, institute: opt.brandName, course: '', tenure: '' }));
    setSelectedInstId(opt.id);
    setInstOpen(false);
    setCourseQuery('');
    setCourseResults([]);
    setTenureProducts([]);
    try {
      const base = process.env.REACT_APP_BASE_URL;
      const res = await fetch(`${base}/products/instituteId/active?instituteId=${opt.id}`, {});
      const json = await res.json();
      const products = json.data || [];
      const sorted = [...products].sort((a, b) => a.tenure - b.tenure);
      setTenureProducts(sorted);
    } catch {
      showToast('Failed to load products for this institute.');
    }
  };

  // ── Course search ──────────────────────────────────────────────────

  const handleCourseSearch = (query) => {
    setCourseQuery(query);
    setForm(p => ({ ...p, course: query }));
    if (courseDebounce.current) clearTimeout(courseDebounce.current);
    if (!query || !selectedInstId) { setCourseResults([]); setCourseOpen(false); return; }
    courseDebounce.current = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL;
        const res = await fetch(
          `${base}/institute/course/search?instituteId=${selectedInstId}&name=${encodeURIComponent(query)}`,
          {}
        );
        const json = await res.json();
        const results = json.data?.courses || [];
        setCourseResults(results);
        setCourseOpen(results.length > 0);
      } catch {
        showToast('Failed to search courses. Please try again.');
      }
    }, 300);
  };

  const selectCourse = (opt) => {
    setCourseQuery(opt.courseName);
    setForm(p => ({ ...p, course: opt.courseName }));
    setCourseOpen(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────
  // API integration points (commented out — wire up during integration phase):
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const nameParts = form.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || firstName;

      const payload = {
        mobile: form.mobile,
        firstName,
        lastName,
        instituteName: form.institute,
        courseName: form.course,
        courseFees: Number(form.fees) || 0,
        panId: form.pan,
        dateOfBirth: form.dob,
        currentState: form.currentState,
        loanTenure: Number(form.tenure) || 0,
        channel:6,
        email: form.email,
        studentName: isStudentBorrower ? form.name : form.studentName,
        ...(selectedProductId ? { productId: selectedProductId } : {}),
      };

      const res = await axiosInstance.post('/summary/create', payload);
      const { userId, applicationId } = res?.data?.data || {};
      setCreatedApp({ userId, applicationId });
      setStep('choose');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create application. Please try again.';
      showToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── OTP / Link handlers ────────────────────────────────────────────

  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const handleSendOtp = async () => {
    setOtpLoading(true);
    try {
      await axiosInstance.post('/login/create-otp', { mobile: form.mobile });
      setStep('otp');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send OTP. Please try again.';
      showToast(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setVerifyLoading(true);
    try {
      // Step 1: verify OTP
      await axiosInstance.post('/login/verify-otp', {
        mobile: form.mobile,
        otp: otpDigits.join(''),
      });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Verification failed. Please try again.';
      showToast(msg);
      setVerifyLoading(false);
      return;
    }

    try {
      // Step 2: submit QEC — saves the OTP consents and moves the application to In Review (+ application PDF + email)
      await axiosInstance.post('/end-user/submit/qec', {
        userId: createdApp.userId,
        latitude: 0,
        longitude: 0,
        doneBy: 'QEC',
      });
      setStep('eligible');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to submit the application. Please try again.';
      showToast(msg);
    } finally {
      setVerifyLoading(false);
    }
  };

  const [withoutOtpLoading, setWithoutOtpLoading] = useState(false);

  const handleSubmitWithoutOtp = async () => {
    setWithoutOtpLoading(true);
    try {
      await axiosInstance.post('/end-user/submit/send-whatsapp-consent', {
        userId: createdApp.userId,
        mobile: form.mobile,
      });
      setStep('app-created');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send consent. Please try again.';
      showToast(msg);
    } finally {
      setWithoutOtpLoading(false);
    }
  };

  const handleSendLink = () => {
    // API integration point: POST trigger-link endpoint with application details
    setStep('link-sent');
  };

  // ── Reset ──────────────────────────────────────────────────────────

  const resetForm = () => {
    setForm({ mobile: '', name: '', institute: '', course: '', fees: '', tenure: '', currentState: '', pan: '', dob: '', email: '', studentName: '' });
    setIsStudentBorrower(false);
    setInstQuery(''); setInstResults([]); setSelectedInstId(null);
    setCourseQuery(''); setCourseResults([]);
    setTenureProducts([]);
    setEmiMap({});
    setSelectedProductId(null);
    setError('');
    setSubmitting(false);
    setCreatedApp({ userId: null, applicationId: null });
    setStep('form');
    setOtpDigits(['', '', '', '', '', '']);
    setConsentChecked(false);
  };

  const currentCards = tenureProducts.filter(p => p.tenure === Number(form.tenure));
  const showProductCards = form.tenure && currentCards.length > 0 && currentCards.every(p => p.productId in emiMap);

  return (
    <div style={{ width: '100%', height: '100%', backgroundColor: '#FFF' }}>
        <div className="elig-page">
          <div className="elig-form-wrapper">

            <div className="elig-welcome">
              <p style={{ fontWeight: '500', fontSize: '1.5em', fontFamily: 'Outfit', margin: 0 }}>
                Welcome ! <span style={{ color: '#d32028', fontFamily: 'Outfit' }}>{decode.display_name}</span>
              </p>
              <p style={{ fontSize: '0.9em', fontFamily: 'Outfit', margin: '4px 0 0', color: '#555' }}>
                Fill in the applicant details below to generate an application link.
              </p>
            </div>

            {/* ── Step: Form ── */}
            {step === 'form' && (
              <form onSubmit={handleSubmit}>
                <Box className="fields-container">
                  <h5 className="form-heading">Applicant Details</h5>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', px: 2, pb: 2 }}>

                    <TextField
                      label="Mobile Number"
                      value={form.mobile}
                      onChange={e => setForm(p => ({ ...p, mobile: e.target.value.replace(/\D/g, '') }))}
                      type="tel"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]{10}', maxLength: 10 }}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />
                    <TextField
                      label="Applicant Name"
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="PAN Number"
                      value={form.pan}
                      onChange={e => setForm(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                      inputProps={{ maxLength: 10, style: { fontFamily: 'Outfit', textTransform: 'uppercase', letterSpacing: '0.05em' } }}
                      required
                      size="small"
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="Date of Birth"
                      type="date"
                      value={form.dob}
                      onChange={e => setForm(p => ({ ...p, dob: e.target.value }))}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx, shrink: true }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="Student Name"
                      value={isStudentBorrower ? form.name : form.studentName}
                      onChange={e => !isStudentBorrower && setForm(p => ({ ...p, studentName: e.target.value }))}
                      disabled={isStudentBorrower}
                      size="small"
                      InputProps={{
                        style: { fontFamily: 'Outfit' },
                        endAdornment: (
                          <InputAdornment position="end">
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={isStudentBorrower}
                                  onChange={e => {
                                    setIsStudentBorrower(e.target.checked);
                                    if (e.target.checked) setForm(p => ({ ...p, studentName: p.name }));
                                    else setForm(p => ({ ...p, studentName: '' }));
                                  }}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#D32028' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#D32028' },
                                  }}
                                />
                              }
                              label={<span style={{ fontSize: '11px', fontFamily: 'Outfit', color: '#888', whiteSpace: 'nowrap' }}>Same as applicant</span>}
                              sx={{ mr: 0, ml: 0.5 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    {showInstSearch && (
                      <Box ref={instDropRef} sx={{ position: 'relative' }}>
                        <TextField
                          label="Institute Name"
                          value={instQuery}
                          onChange={e => handleInstSearch(e.target.value)}
                          onFocus={() => instResults.length > 0 && setInstOpen(true)}
                          autoComplete="off"
                          required
                          size="small"
                          InputProps={tfInputProps}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        />
                        {instOpen && (
                          <ul className="elig-dropdown">
                            {instResults.map(opt => (
                              <li key={opt.id} onMouseDown={() => selectInst(opt)}>{opt.brandName}</li>
                            ))}
                          </ul>
                        )}
                      </Box>
                    )}

                    <Box ref={courseDropRef} sx={{ position: 'relative' }}>
                      <TextField
                        label="Course Name"
                        value={selectedInstId ? courseQuery : form.course}
                        onChange={e => selectedInstId ? handleCourseSearch(e.target.value) : setForm(p => ({ ...p, course: e.target.value }))}
                        onFocus={() => courseResults.length > 0 && setCourseOpen(true)}
                        autoComplete="off"
                        required
                        size="small"
                        InputProps={tfInputProps}
                        InputLabelProps={{ sx: tfLabelSx }}
                        sx={{ ...tfSx, width: '100%', mb: 0 }}
                      />
                      {courseOpen && (
                        <ul className="elig-dropdown">
                          {courseResults.map(opt => (
                            <li key={opt.id} onMouseDown={() => selectCourse(opt)}>{opt.courseName}</li>
                          ))}
                        </ul>
                      )}
                    </Box>

                    {showInstSearch ? (
                      <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px 24px' }}>
                        <TextField
                          label="Course Fees"
                          type="number"
                          value={form.fees}
                          onChange={e => setForm(p => ({ ...p, fees: e.target.value }))}
                          onWheel={e => e.target.blur()}
                          inputProps={{ min: 0 }}
                          required
                          size="small"
                          InputProps={tfInputProps}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        />
                        <TextField
                          select
                          label="Tenure"
                          value={form.tenure}
                          onChange={e => { setForm(p => ({ ...p, tenure: e.target.value })); setSelectedProductId(null); }}
                          required
                          size="small"
                          InputProps={tfInputProps}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        >
                          <MenuItem value="">Select tenure</MenuItem>
                          {(tenureProducts.length > 0
                            ? Array.from(new Set(tenureProducts.map(p => p.tenure)))
                            : TENURE_OPTIONS
                          ).map(t => (
                            <MenuItem key={t} value={String(t)}>{t} months</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="State"
                          value={form.currentState}
                          onChange={e => setForm(p => ({ ...p, currentState: e.target.value }))}
                          required
                          size="small"
                          InputProps={{
                            style: { fontFamily: 'Outfit' },
                            endAdornment: form.currentState ? (
                              <InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}>
                                <IconButton
                                  size="small"
                                  onMouseDown={e => { e.stopPropagation(); setForm(p => ({ ...p, currentState: '' })); }}
                                  sx={{ p: 0.3 }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                    <path d="M1 1l12 12M13 1L1 13" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          }}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        >
                          <MenuItem value="">Select state</MenuItem>
                          {statesList.map(s => (
                            <MenuItem key={s.label} value={s.label}>{s.label}</MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    ) : (
                      <>
                        <TextField
                          label="Course Fees"
                          type="number"
                          value={form.fees}
                          onChange={e => setForm(p => ({ ...p, fees: e.target.value }))}
                          onWheel={e => e.target.blur()}
                          inputProps={{ min: 0 }}
                          required
                          size="small"
                          InputProps={tfInputProps}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        />
                        <TextField
                          select
                          label="Tenure"
                          value={form.tenure}
                          onChange={e => { setForm(p => ({ ...p, tenure: e.target.value })); setSelectedProductId(null); }}
                          required
                          size="small"
                          InputProps={tfInputProps}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        >
                          <MenuItem value="">Select tenure</MenuItem>
                          {(tenureProducts.length > 0
                            ? Array.from(new Set(tenureProducts.map(p => p.tenure)))
                            : TENURE_OPTIONS
                          ).map(t => (
                            <MenuItem key={t} value={String(t)}>{t} months</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="State"
                          value={form.currentState}
                          onChange={e => setForm(p => ({ ...p, currentState: e.target.value }))}
                          required
                          size="small"
                          InputProps={{
                            style: { fontFamily: 'Outfit' },
                            endAdornment: form.currentState ? (
                              <InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}>
                                <IconButton
                                  size="small"
                                  onMouseDown={e => { e.stopPropagation(); setForm(p => ({ ...p, currentState: '' })); }}
                                  sx={{ p: 0.3 }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                    <path d="M1 1l12 12M13 1L1 13" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          }}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        >
                          <MenuItem value="">Select state</MenuItem>
                          {statesList.map(s => (
                            <MenuItem key={s.label} value={s.label}>{s.label}</MenuItem>
                          ))}
                        </TextField>
                      </>
                    )}

                  </Box>
                </Box>

                {showProductCards && (
                  <Box className="fields-container">
                    <h5 className="form-heading">Select Product</h5>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, px: 1, pb: 1 }}>
                      {currentCards.map(p => (
                        <div
                          key={p.productId}
                          onClick={() => setSelectedProductId(p.productId)}
                          className={`elig-product-card${selectedProductId === p.productId ? ' selected' : ''}`}
                        >
                          <div className="elig-product-row">
                            <div className="elig-product-col">
                              <span>Facility Type</span>
                              <p>{FACILITY_TYPE[p.facilityType] ?? p.facilityType}</p>
                            </div>
                            <div className="elig-product-col">
                              <span>Tenure</span>
                              <p>{p.tenure} months</p>
                            </div>
                            {emiMap[p.productId] != null && (
                              <div className="elig-product-col">
                                <span>EMI Amount</span>
                                <p>₹{Number(emiMap[p.productId]).toLocaleString('en-IN')}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </Box>
                  </Box>
                )}

                {error && <p className="elig-error">{error}</p>}

                <div className="elig-submit-row">
                  <button type="submit" className="elig-submit-btn" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Proceed'}
                  </button>
                </div>
              </form>
            )}


          </div>
        </div>

      {/* ── Choose action modal ── */}
      {step === 'choose' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal">
            <h2>Application Created</h2>
            <p className="elig-modal-hint">
              Choose how to proceed with <strong>{form.name || 'the applicant'}</strong>
              {form.mobile ? ` (${form.mobile})` : ''}.
            </p>
            <div className="elig-choose-cards">
              <button className="elig-choose-card" onClick={handleSendOtp} disabled={otpLoading}>
                <div className="elig-choose-card-header">
                  <div className="elig-choose-card-icon">
                    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="elig-choose-card-title">Submit with OTP</span>
                </div>
                <span className="elig-choose-card-desc">An OTP will be sent to the applicant's mobile. Submitting it acts as their consent for submitting the application.</span>
              </button>

              <button className="elig-choose-card" onClick={handleSubmitWithoutOtp} disabled={withoutOtpLoading}>
                <div className="elig-choose-card-header">
                  <div className="elig-choose-card-icon">
                    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="elig-choose-card-title">Submit without OTP</span>
                </div>
                <span className="elig-choose-card-desc">Sends a WhatsApp consent request to the applicant's mobile. They can review the terms and confirm to proceed.</span>
              </button>
            </div>
            <button className="elig-close-btn" onClick={() => setStep('form')}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Link sent success modal ── */}
      {step === 'link-sent' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal" style={{ textAlign: 'center' }}>
            <div className="elig-link-sent-icon">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#D32028" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h2 style={{ marginTop: '12px', marginBottom: '8px' }}>Link Sent Successfully</h2>
            <p className="elig-modal-hint">
              A self-service link has been sent to <strong>{form.mobile}</strong>.
            </p>
            <button className="elig-close-btn" style={{ marginTop: '16px' }} onClick={resetForm}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Eligible success modal ── */}
      {step === 'eligible' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal" style={{ textAlign: 'center' }}>
            <div className="elig-link-sent-icon">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#D32028" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 style={{ marginTop: '12px', marginBottom: '4px' }}>Application Submitted</h2>
            <p className="elig-modal-hint" style={{ marginBottom: '16px' }}>
              Thanks for your submission. Our team will proceed with further steps on this application.
            </p>
            <button className="elig-close-btn" onClick={resetForm}>Close</button>
          </div>
        </div>
      )}

      {/* ── App created success modal ── */}
      {step === 'app-created' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal" style={{ textAlign: 'center' }}>
            <div className="elig-link-sent-icon">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#D32028" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 style={{ marginTop: '12px', marginBottom: '8px' }}>Application Created Successfully</h2>
            <p className="elig-modal-hint">
              The application for <strong>{form.name || form.mobile}</strong> has been created and will be reviewed by our team.
            </p>
            <button className="elig-close-btn" style={{ marginTop: '16px' }} onClick={resetForm}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── OTP entry modal ── */}
      {step === 'otp' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal">
            <h2>Verify OTP</h2>
            <p className="elig-modal-hint">
              Enter the OTP sent to <strong>{form.mobile}</strong> to give consent for submitting the application.
            </p>
            <div className="elig-otp-boxes-row">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  autoFocus={i === 0}
                  onChange={e => handleOtpDigitChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  onPaste={handleOtpPaste}
                  className="elig-otp-box"
                />
              ))}
            </div>
            <div className="elig-consent-row" style={{ marginBottom: '16px' }}>
              <input
                type="checkbox"
                id="cibil-consent"
                checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)}
                className="elig-consent-check"
              />
              <label htmlFor="cibil-consent" className="elig-consent-label">
                By proceeding, you agree to our{' '}
                <a href="https://www.feemonk.com/terms-and-condition" target="_blank" rel="noopener noreferrer" className="elig-consent-link">Terms and Conditions</a>
                {' '}and{' '}
                <a href="https://www.feemonk.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="elig-consent-link">Privacy Policy</a>
                , and authorize feemonk to get a background check and a consumer credit report on me..
              </label>
            </div>
            <button
              type="button"
              className="elig-submit-btn"
              style={{ width: '100%', height: '42px', borderRadius: '6px', marginBottom: '12px' }}
              disabled={!consentChecked || otpDigits.some(d => d === '') || verifyLoading}
              onClick={handleVerifyOtp}
            >
              {verifyLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <div className="elig-fallback-row">
              <button type="button" className="elig-fallback-link" onClick={handleSendOtp}>
                Resend OTP
              </button>
              <span className="elig-fallback-sep">·</span>
              <button type="button" className="elig-fallback-link" onClick={() => setStep('choose')}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toastMsg}
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />
    </div>
  );
}
